import { useSyncExternalStore, useCallback } from 'react';
import { createPersistentStore } from '@shared/shell/storeUtils';
import { BP } from '@shared/hooks/use-breakpoint';

export interface LayoutState {
  leftCollapsed: boolean;
  rightOpen: boolean;
}

const isInitialDesktop =
  typeof window !== 'undefined' ? window.innerWidth >= BP.xl : true;

export const layoutStore = createPersistentStore<LayoutState>(
  {
    leftCollapsed: false,
    rightOpen: isInitialDesktop,
  },
  'helios.prefs.v1:layout'
);

let mobileRightOpen = false;
const mobileListeners = new Set<() => void>();

function notifyMobile() {
  mobileListeners.forEach((l) => l());
}

export function useLayoutPrefs() {
  const persisted = useSyncExternalStore(
    layoutStore.subscribe,
    layoutStore.getSnapshot,
    layoutStore.getSnapshot
  );

  const mobileOpen = useSyncExternalStore(
    (cb) => {
      mobileListeners.add(cb);
      return () => {
        mobileListeners.delete(cb);
      };
    },
    () => mobileRightOpen,
    () => false
  );

  const isWide = typeof window !== 'undefined' ? window.innerWidth >= BP.xl : true;
  const rightOpen = isWide ? persisted.rightOpen : mobileOpen;

  const toggleLeft = useCallback(() => {
    layoutStore.set((prev) => ({ ...prev, leftCollapsed: !prev.leftCollapsed }));
  }, []);

  const setLeftCollapsed = useCallback((collapsed: boolean) => {
    layoutStore.set((prev) => ({ ...prev, leftCollapsed: collapsed }));
  }, []);

  const toggleRight = useCallback(() => {
    const wide = typeof window !== 'undefined' ? window.innerWidth >= BP.xl : true;
    if (wide) {
      layoutStore.set((prev) => ({ ...prev, rightOpen: !prev.rightOpen }));
    } else {
      mobileRightOpen = !mobileRightOpen;
      notifyMobile();
    }
  }, []);

  const setRightOpen = useCallback((open: boolean) => {
    const wide = typeof window !== 'undefined' ? window.innerWidth >= BP.xl : true;
    if (wide) {
      layoutStore.set((prev) => ({ ...prev, rightOpen: open }));
    } else {
      mobileRightOpen = open;
      notifyMobile();
    }
  }, []);

  return {
    leftCollapsed: persisted.leftCollapsed,
    rightOpen,
    toggleLeft,
    toggleRight,
    setRightOpen,
    setLeftCollapsed,
  };
}
